import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { get } from "@rails/request.js";
import { Modal, useModal } from "./Modal";

const ProgressBar = ({ progressPercentage }) => {
  let color = 'bg-sspurple-alt'
  if (progressPercentage > 10 && progressPercentage <= 30) {
    color = 'bg-gradient-to-r from-sspurple-alt to-sspurple'
  } else if (progressPercentage > 30 && progressPercentage <= 50) {
    color = 'bg-gradient-to-r from-sspurple-alt via-sspurple to-ssblue'
  } else if (progressPercentage > 50 && progressPercentage <= 70) {
    color = 'bg-gradient-to-r from-sspurple via-ssblue to-ssblue-alt'
  } else if (progressPercentage > 70 && progressPercentage <= 90) {
    color = 'bg-gradient-to-r from-ssblue via-ssblue-alt to-ssgreen'
  } else if (progressPercentage > 90) {
    color = 'bg-gradient-to-r from-ssblue via-ssgreen to-ssgreen-alt'
  }
  return (
    <div className='h-2 w-full bg-gray-300'>
      <div style={{ width: `${progressPercentage}%` }} className={`h-full ${color}`}> </div>
    </div>
  )
}

const canRecord = typeof window !== 'undefined'
  && typeof window.MediaRecorder !== 'undefined'
  && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || ''
}

const extForMime = (mime) => {
  if (!mime) return 'webm'
  if (mime.includes('mp4')) return 'm4a'
  if (mime.includes('ogg')) return 'ogg'
  return 'webm'
}

const formatTime = (s) => {
  const m = Math.floor(s / 60)
  const sec = (s % 60).toString().padStart(2, '0')
  return `${m}:${sec}`
}

export const AudioUploader = ({ initialUrl, setUrl }) => {
  const [uploading, setUploading] = useState(false)
  const audioRef = useRef(null)
  const [progressPercentage, setProgressPercentage] = useState(0.0)
  const [file, setFile] = useState(null)
  const { modalOpen, setModalOpen } = useModal()

  const [mode, setMode] = useState(canRecord ? 'record' : 'upload')
  const [recording, setRecording] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [recordError, setRecordError] = useState(null)
  const mediaRecorderRef = useRef(null)
  const chunksRef = useRef([])
  const streamRef = useRef(null)
  const timerRef = useRef(null)

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
    }
  }, [])

  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: 'audio/*', maxFiles: 1, maxSize: 1000000000 })

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const reset = () => {
    setFile(null)
    setProgressPercentage(0)
    setUploading(false)
    setElapsed(0)
    setRecordError(null)
    if (audioRef.current) audioRef.current.src = initialUrl || ''
  }

  const startRecording = async () => {
    setRecordError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const mimeType = pickMimeType()
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      mediaRecorderRef.current = mr
      chunksRef.current = []

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
      }
      mr.onstop = () => {
        const type = mr.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type })
        const ext = extForMime(type)
        const recordedFile = new File([blob], `recording-${Date.now()}.${ext}`, { type })
        setFile(recordedFile)
        stopStream()
      }

      mr.start()
      setRecording(true)
      setElapsed(0)
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000)
    } catch (err) {
      stopStream()
      setRecordError(
        err && err.name === 'NotAllowedError'
          ? 'Microphone access was denied. Please allow microphone access and try again.'
          : 'Could not start recording. Check that your device has a microphone and try again.'
      )
      setRecording(false)
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    setRecording(false)
  }

  const switchMode = (newMode) => {
    if (recording) stopRecording()
    reset()
    setMode(newMode)
  }

  useEffect(() => () => stopStream(), [])

  const uploadFile = async () => {
    setUploading(true)
    const key = `audio-${file.name}`
    const response = await get(`/api/v1/uploads/r2_hash?key=${key}`, {
      responseKind: "application/json",
    })

    if (response.ok) {
      const json = await response.json

      const xhr = new XMLHttpRequest()
      await new Promise((resolve) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            setProgressPercentage((event.loaded / event.total) * 100)
          }
        })
        xhr.addEventListener("loadend", () => {
          resolve(xhr.readyState === 4 && xhr.status === 200)
          setUploading(false)
          setUrl(json.base_url + '/' + key)
          setModalOpen(false)
        })
        xhr.open("PUT", json.signed_url, true)
        xhr.setRequestHeader("Content-Type", file.type)
        xhr.send(file)
      })
    } else {
      console.log('refresh the page. your session is expired')
      setUploading(false)
    }
  }

  useEffect(() => {
    if (!file) return
    if (!file.type.startsWith('audio')) {
      setRecordError('That file is not recognized as audio. Try MP3, WAV, M4A, or WEBM.')
      return
    }
    if (audioRef.current) audioRef.current.src = URL.createObjectURL(file)
  }, [file])

  const TabButton = ({ value, label }) => (
    <button
      onClick={() => switchMode(value)}
      className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
        mode === value ? 'bg-ssblue text-white' : 'text-slate-700 hover:bg-slate-100'
      }`}
    >
      {label}
    </button>
  )

  return <>
    <audio ref={audioRef} controls className={`h-96 w-96 mx-auto object-cover rounded-md cursor-pointer shadow-lg border border-gray-400 border-dashed border-2 ${!initialUrl && !file ? 'hidden' : ''}`} src={initialUrl} onClick={() => setModalOpen(true)} />
    {!file && !initialUrl && (
      <button
        className="relative block mt-4 sm:mx-auto w-lg rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        onClick={() => setModalOpen(true)}
      >
        <img src="https://images.shosay.com/icon-message-speech.png" className="mx-auto h-16 w-16" />
        <span className="mt-2 block text-sm font-semibold text-gray-900">Record or upload audio</span>
      </button>
    )}
    {modalOpen && (
      <Modal modalOpen={modalOpen} setModalOpen={setModalOpen} icon="audio" title="Add audio" maxWidth="max-w-md">
        <div className="relative p-2 sm:p-8">
          <div className="sm:text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Add Audio</h2>
          </div>

          {canRecord && !file && (
            <div className="mt-4 flex gap-1 bg-white rounded-lg p-1 border-2 border-black">
              <TabButton value="record" label="Record" />
              <TabButton value="upload" label="Upload" />
            </div>
          )}

          <div className="w-full mx-auto mt-4 sm:mt-6">
            <audio
              ref={audioRef}
              controls
              className={`h-10 w-full rounded-md shadow-lg border border-2 border-black ${!file && !initialUrl ? 'hidden' : ''}`}
              src={initialUrl}
            />

            {mode === 'upload' && !file && (
              <div
                className="bg-gray-50 mt-3 relative block min-h-20 w-full border-2 border-gray-300 flex flex-col justify-center cursor-pointer border-dashed rounded-lg p-6 sm:p-12 text-center hover:border-gray-400"
                {...getRootProps()}
              >
                <input {...getInputProps()} />
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  Click or drop an <b>audio</b> file to upload.
                </span>
              </div>
            )}

            {mode === 'record' && !file && (
              <div className="mt-4 flex flex-col items-center gap-3 py-4">
                {!recording ? (
                  <button
                    onClick={startRecording}
                    className="h-20 w-20 rounded-full bg-ssred border-2 border-black flex items-center justify-center text-white shadow-lg hover:scale-105 transition-transform"
                    aria-label="Start recording"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-10 w-10">
                      <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Z" />
                      <path d="M5 11a1 1 0 1 0-2 0 9 9 0 0 0 8 8.94V22a1 1 0 1 0 2 0v-2.06A9 9 0 0 0 21 11a1 1 0 1 0-2 0 7 7 0 1 1-14 0Z" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={stopRecording}
                    className="h-20 w-20 rounded-full bg-ssred border-2 border-black flex items-center justify-center text-white shadow-lg animate-pulse"
                    aria-label="Stop recording"
                  >
                    <div className="h-7 w-7 bg-white rounded-sm" />
                  </button>
                )}
                <div className="text-xl font-mono font-semibold tabular-nums">{formatTime(elapsed)}</div>
                <div className="text-sm text-slate-600">
                  {recording ? 'Tap to stop' : 'Tap to start recording'}
                </div>
              </div>
            )}

            {recordError && (
              <div className="mt-3 text-sm text-ssred">{recordError}</div>
            )}

            {file && (
              <div className="bg-gray-50 w-full mt-2 border-2 rounded-lg border-gray-300 p-2">
                <div className="mt-2 text-sm text-gray-500 break-words">
                  {file.name} - {(file.size / 1000 / 1000).toFixed(2)} MB
                </div>
                {uploading && <ProgressBar progressPercentage={progressPercentage} />}
                <div className="mt-2 flex justify-center gap-x-3">
                  <button
                    onClick={reset}
                    className="mt-2 group relative inline-flex h-10 items-center justify-center overflow-hidden rounded-md bg-ssbeige px-3 py-2 text-slate-900 border-2 border-black"
                  >
                    <span className="absolute h-0 w-0 rounded-full bg-ssbeige-alt transition-all duration-300 group-hover:h-56 group-hover:w-32"></span>
                    <span className="relative">{mode === 'record' ? 'Re-record' : 'Reset'}</span>
                  </button>
                  <button
                    onClick={uploadFile}
                    className="mt-2 group relative inline-flex h-10 items-center justify-center overflow-hidden rounded-md bg-ssblue px-3 py-2 text-white border-2 border-black"
                  >
                    <span className="absolute h-0 w-0 rounded-full bg-sspurple transition-all duration-300 group-hover:h-56 group-hover:w-32"></span>
                    <span className="relative">Save</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    )}
  </>
}
